import styles from './Tag.module.css';

import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Icon from '../../helpers/components/Icon';

const COLOR_MAP = {
  primary: 'var(--color-brand)',
  accent: 'var(--color-accent)',
  error: 'var(--color-error)',
  secondary: 'var(--color-secondary)',
};

const ALLOWED_VARIANTS = ['outlined', 'flat'];

/**
 * **Tag** — это абстрактный компонент для отображения текстовых меток и информационных тегов.
 * Компонент поддерживает различные стили и цвета, что делает его гибким для использования
 * в качестве информационных меток для UI элементов.
 *
 * ### Описание параметров:
 *
 * @param {string} text - Текст тега.
 * @param {'primary'|'accent'|'error'|'secondary'} [color='primary'] - Цвет.
 *    Допустимые значения:
 *    - `'primary'` — основной цвет.
 *    - `'accent'` — акцентный цвет.
 *    - `'error'` — цвет ошибки.
 *    - `'secondary'` — системный доп. цвет.
 * @param {'outlined'|'flat'} [variant='flat'] - Вариация стиля.
 *    Допустимые значения
 *    - `'outlined'` — тег с контуром.
 *    - `'flat'` — "плоский" тег (с фоном).
 * @param {{}} icon - Путь или ссылка на изображение иконки (src), отображаемой в кнопке.
 * @param {string} [className] - Дополнительные классы для стилизации кнопки.
 *
 * @component
 * @example
 * // Простой тег
 * <Tag text="Активен" color="success" variant="flat" />
 *
 * // Тег с иконкой
 * <Tag
 *   text="В обработке"
 *   icon={statusIcon}
 *   color="warning"
 *   variant="outlined"
 * />
 */
const Tag = ({ text, color = 'primary', variant = 'flat', icon, className }) => {
  let resolvedColor = COLOR_MAP[color] || color;

  if (!ALLOWED_VARIANTS.includes(variant)) variant = 'flat';

  const tagClasses = classNames(
    styles.tag,
    styles[`color-${color}`],
    styles[`variant-${variant}`],
    className,
  );

  return (
    <span
      className={tagClasses}
      style={{
        color: variant === 'flat' ? 'white' : resolvedColor,
        borderColor: resolvedColor,
        backgroundColor: variant === 'flat' ? resolvedColor : 'transparent',
      }}
    >
      {icon && (
        <Icon
          src={icon}
          color={variant === 'flat' ? 'white' : resolvedColor}
          width={`16px`}
          height={`16px`}
          className={styles.icon}
        />
      )}
      <span className={styles.text}>{text}</span>
    </span>
  );
};

Tag.propTypes = {
  /** Текст, отображаемый внутри тега */
  text: PropTypes.string.isRequired,
  /**
   * Цвет тега. Может быть одним из предопределенных значений или любым CSS-цветом.
   * Предопределенные значения: 'primary', 'accent', 'error', 'secondary'
   */
  color: PropTypes.string,
  /**
   * Вариация стиля тега.
   * Допустимые значения: 'outlined', 'flat'
   */
  variant: PropTypes.oneOf(ALLOWED_VARIANTS),
  /** Опциональная иконка, отображаемая перед текстом */
  icon: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  /** Дополнительные классы для стилизации тега */
  className: PropTypes.string,
};

export default Tag;
